import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { useCartStore } from '@/stores/cart'
import type { Product } from '@/types/product'
import { route as ziggyRoute } from '../../../vendor/tightenco/ziggy/dist/index.esm.js'
import { Ziggy } from '../ziggy'

// Create route helper with Ziggy config
const route = (name?: string, params?: any, absolute?: boolean) => {
  return ziggyRoute(name, params, absolute, Ziggy)
}

/**
 * Shared composable for cart operations
 * Eliminates duplicated cart handling logic between components
 */
export function useCartOperations() {
  const cartStore = useCartStore()
  const { addToCart: addToCartStore } = cartStore

  // Modal state
  const showModal = ref(false)
  const modalProductName = ref('')
  const modalQuantity = ref(0)

  /**
   * Close the add to cart modal
   */
  const closeModal = () => {
    showModal.value = false
    modalProductName.value = ''
    modalQuantity.value = 0
  }

  /**
   * Handle adding a product to cart (shared logic)
   */
  const handleAddToCart = async (product: Product, quantity: number) => {
    try {
      // Add to Pinia store for UI reactivity
      addToCartStore(product, quantity)

      // Show confirmation modal immediately for better UX
      modalProductName.value = product.name
      modalQuantity.value = quantity
      showModal.value = true

      // Send to backend (happens in background)
      const quantities: Record<string, number> = {}
      quantities[product.sku] = quantity

      router.post(route('cart'), {
        quantities
      }, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          // Backend confirmed - modal is already showing
        },
        onError: () => {
          // Handle error - close modal, revert store change, and show error
          showModal.value = false
          console.error('Failed to add item to cart')
          alert(trans('Fehler beim Hinzufügen zum Warenkorb. Bitte versuchen Sie es erneut.'))

          // Revert the store change
          const currentQty = cartStore.getQuantity(product.sku)
          cartStore.updateQuantity(product.sku, Math.max(0, currentQty - quantity))
        }
      })
    } catch (error) {
      console.error('Error adding to cart:', error)
      showModal.value = false
      alert(trans('Ein unerwarteter Fehler ist aufgetreten.'))
    }
  }

  return {
    // Modal state
    showModal,
    modalProductName,
    modalQuantity,

    // Methods
    closeModal,
    handleAddToCart,
  }
}